import {
  AfterContentChecked,
  AfterContentInit,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DsSelectBackground, DsSelectSize, DsSelectState, DsSelectType } from '../../select.interfaces';
import { DsSelectOptionComponent } from '../select-option/select-option.component';
import { Store } from '@skyeng/libs/shared/store';
import { combineLatest, from, Observable, of } from 'rxjs';
import { DecorateUntilDestroy, takeUntilDestroyed } from '@skyeng/libs/shared/rxjs';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { DsBackgroundDirective, DsSizeDirective } from '@skyeng/libs/shared/ui-kit-v2/base';
import { delay, filter, map, shareReplay, startWith } from 'rxjs/operators';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { DsFormFieldSize } from '@skyeng/libs/shared/ui-kit-v2/form-field';
import { DsBreakpointService } from '@skyeng/libs/shared/ui-kit-v2/layout';
import defaultArrow1 from './images/default-arrow.svg?raw';
import defaultArrowSmall1 from './images/default-arrow-small.svg?raw';
import inlineArrow1 from './images/inline-arrow.svg?raw';
import inlineArrowSmall1 from './images/inline-arrow-small.svg?raw';
import validIcon1 from './images/valid-icon.svg?raw';
import validIconSmall1 from './images/valid-icon-small.svg?raw';
import invalidIcon1 from './images/invalid-icon.svg?raw';
import invalidIconSmall1 from './images/invalid-icon-small.svg?raw';

const DEFAULT_BACKGROUND: DsSelectBackground = 'light';
const DEFAULT_SIZE: DsSelectSize = 'm';

@DecorateUntilDestroy()
@Component({
  selector: 'ds-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DsSelectComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class DsSelectComponent
  implements ControlValueAccessor, AfterContentInit, AfterContentChecked, OnInit, AfterViewInit, OnDestroy, OnChanges
{
  @Input() value = '';
  @Input() size: DsSelectSize;
  @Input() type: DsSelectType = 'default';
  @Input() state: DsSelectState = 'default';
  @Input() background: DsSelectBackground;
  @Input() disabled = false;
  @Input() name?: string;
  @Input() id?: string;
  @Input() placeholder?: string;

  @Output() valueChange = new EventEmitter<string>();

  @ContentChildren(DsSelectOptionComponent) optionList: QueryList<DsSelectOptionComponent>;

  @ViewChild('leftIcon', { static: false }) leftIconRef: ElementRef<HTMLElement>;
  @ViewChild('widthCalculator', { static: false }) widthCalculatorRef: ElementRef<HTMLElement>;
  @ViewChildren('select') selectRefs: QueryList<ElementRef<HTMLSelectElement>>;

  private images = {
    defaultArrow: defaultArrow1,
    defaultArrowSmall: defaultArrowSmall1,
    inlineArrow: inlineArrow1,
    inlineArrowSmall: inlineArrowSmall1,
    validIcon: validIcon1,
    validIconSmall: validIconSmall1,
    invalidIcon: invalidIcon1,
    invalidIconSmall: invalidIconSmall1,
  };
  private store = new Store({
    value: '',
    disabled: false,
    hasLeftIcon: false,
    hasRightIcon: false,
    selectWidth: null as number | null,
    background: undefined as DsSelectBackground | undefined, // actual value
    inputBackground: undefined as DsSelectBackground | undefined, // @Input value
    size: undefined as DsSelectSize | undefined, // actual value
    inputSize: undefined as DsSelectSize | undefined, // @Input value
  });

  options$: Observable<DsSelectOptionComponent[]>;
  data$ = this.store.select();

  constructor(
    @Optional() private dsBackgroundDirective: DsBackgroundDirective,
    @Optional() private dsSizeDirective: DsSizeDirective,
    private dsBreakpointService: DsBreakpointService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.initBackground();
    this.initSize();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.value) {
      this.writeValue(this.value);
    }
    if (changes.disabled) {
      this.setDisabledState(this.disabled);
    }
    if (changes.background) {
      this.store.updateFields({ inputBackground: this.background });
    }
    if (changes.size) {
      this.store.updateFields({ inputSize: this.size });
    }
  }

  ngAfterContentChecked(): void {
    this.updatePartsVisibility();
  }

  ngAfterContentInit(): void {
    this.options$ = this.optionList.changes.pipe(
      startWith(this.optionList.toArray()),
      map(() => this.optionList.toArray()),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  ngAfterViewInit(): void {
    this.initSelectWidth();

    // без этого не добавляется класс -has-left-icon и текст периодически наезжает на иконку
    this.updatePartsVisibility();
  }

  ngOnDestroy(): void {}

  writeValue(value: string): void {
    this.store.updateFields({ value: value || '' });
  }

  registerOnChange(fn: () => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.store.updateFields({ disabled: isDisabled });
  }

  onSelectTouch(): void {
    this.onTouched();
  }

  onSelectChange(event: Event): void {
    const newValue = (event.target as HTMLSelectElement).value;
    this.store.updateFields({ value: newValue });
    this.onChange(newValue);
    this.valueChange.emit(newValue);
  }

  getSize(): DsFormFieldSize {
    const isXsStoredSize = this.store.get('size') === 'xs';
    if (this.dsBreakpointService.isSmall()) {
      return isXsStoredSize ? 'xs' : 's';
    }
    return this.store.get('size')!;
  }

  getArrow(): string {
    if (this.type === 'inline') {
      if (this.size === 'l') {
        return this.images.inlineArrow;
      }
      return this.images.inlineArrowSmall;
    }
    if (this.isSmallSize(this.size)) {
      return this.images.defaultArrowSmall;
    }
    return this.images.defaultArrow;
  }

  getValidIcon(): string {
    if (this.isSmallSize(this.size)) {
      return this.images.validIconSmall;
    }
    return this.images.validIcon;
  }

  getInvalidIcon(): string {
    if (this.isSmallSize(this.size)) {
      return this.images.invalidIconSmall;
    }
    return this.images.invalidIcon;
  }

  private calculateWidth(): void {
    const selfWidth = this.widthCalculatorRef.nativeElement.offsetWidth;
    const rightPadding = this.size === 'l' ? 26 : 18;
    const width = selfWidth + rightPadding;

    this.store.updateFields({ selectWidth: width ?? null });
  }

  private onChange(_: unknown): void {}

  private onTouched(): void {}

  private updatePartsVisibility(): void {
    if (this.leftIconRef) {
      this.store.updateFields({ hasLeftIcon: !!this.leftIconRef.nativeElement.children.length });

      // без этого не добавляется класс -has-left-icon и текст периодически наезжает на иконку
      this.changeDetectorRef.detectChanges();
    }
  }

  private initBackground(): void {
    const inheritedBackground$ = this.dsBackgroundDirective ? this.dsBackgroundDirective.observe() : of(null);
    combineLatest([this.store.select('inputBackground'), inheritedBackground$])
      .pipe(
        map(([inputBackground, inheritBackground]) => inputBackground ?? inheritBackground ?? DEFAULT_BACKGROUND),
        takeUntilDestroyed(this),
      )
      .subscribe(background => {
        this.store.updateFields({ background });
      });
  }

  private initSize(): void {
    const inheritedSize$ = this.dsSizeDirective ? this.dsSizeDirective.observe() : of(null);
    combineLatest([this.store.select('inputSize'), inheritedSize$])
      .pipe(
        map(([inputSize, inheritSize]) => inputSize ?? inheritSize ?? DEFAULT_SIZE),
        takeUntilDestroyed(this),
      )
      .subscribe(size => {
        this.store.updateFields({ size });
      });
  }

  private initSelectWidth(): void {
    const selectRef$ = this.selectRefs.changes.pipe(
      map(() => this.selectRefs.toArray()),
      startWith(this.selectRefs.toArray()),
      map(refs => refs[0]),
    );
    const fontsReady$ =
      this.type === 'inline' && document.fonts ? from(document.fonts.ready).pipe(startWith(undefined)) : of(undefined);
    // wait for selected option change in DOM
    const value$ = this.store.select('value').pipe(delay(0), startWith(undefined));

    combineLatest([selectRef$, value$, fontsReady$])
      .pipe(
        filter(() => this.type === 'inline'),
        map(([nativeSelectRef]) => {
          const selectElement = nativeSelectRef?.nativeElement;
          const selectedOptionText = selectElement?.options[selectElement.selectedIndex].textContent;

          return selectedOptionText ?? this.placeholder ?? '';
        }),
        takeUntilDestroyed(this),
      )
      .subscribe(textContent => {
        this.updateWidthCalculatorContent(textContent);
        this.calculateWidth();
      });
  }

  private updateWidthCalculatorContent(textContent: string): void {
    // eslint-disable-next-line functional/immutable-data
    this.widthCalculatorRef.nativeElement.textContent = textContent;
  }

  private isSmallSize(size: DsSelectSize): boolean {
    return ['xs', 's'].includes(size);
  }
}
